
import { useRef, useEffect, useState } from 'react';
import { useMouseInteraction } from './useMouseInteraction';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  PRODUCTS,
  SATELLITE_ANIMATIONS,
  SATELITE_OBJECTS,
  animateProductToPosition,
  animateProductToCenter,
  animateOriginalProductUp,
  animateOriginalProductOut,
  fadeInParaTusAxilas,
  fadeInForAllTheBody,
  startSubmeshSpin,
  stopSubmeshSpin,
  animateSatelliteMesh,
  disposeThreeObjects
} from '../utils/arUtils';


window.MINDAR_GLOBAL_REGISTRY = window.MINDAR_GLOBAL_REGISTRY || {
  instance: null,
  isSetup: false,
  cleanupInProgress: false,
  containerId: null
};


let globalARInstance = null;
let isGlobalSetup = false;
let productsSetup = false;
let globalCleanupInProgress = false;

export const useMindAR = (containerRef, onProductSelect, onShowHelp, options = {}) => {
  const mindArRef = useRef();
  const [isARReady, setIsARReady] = useState(false);
  const [satellitesVisible, setSatellitesVisible] = useState(false);
  const [isTracking, setIsTracking] = useState(false);

  // Extract options with defaults
  const { disableProductInfoClick = false } = options;

  const isSetupRef = useRef(false);
  const satelliteGroupsRef = useRef([]);
  const hideSatellitesRef = useRef(null);
  const selectedProductRef = useRef(null);
  const isTrackingRef = useRef(false);
  const pendingAnimationsRef = useRef([]);

  // Initialize mouse interaction hook with a callback ref
  const { setupEventListeners, removeEventListeners } = useMouseInteraction(containerRef, (...args) => handleSelection(...args));


  const cleanupExistingMindAR = () => {
    globalCleanupInProgress = true;

    if (pendingAnimationsRef.current.length > 0) {
      pendingAnimationsRef.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      pendingAnimationsRef.current = [];
    }

    if (globalARInstance) {
      try {
        if (globalARInstance.renderer) {
          globalARInstance.renderer.setAnimationLoop(null);
        }
        globalARInstance.stop();
        globalARInstance = null;
      } catch (error) {
        console.error('Error stopping existing instance:', error);
      }
    }

    const existingElements = document.querySelectorAll('.mindar-ui-overlay, .mindar-ui-loading, .mindar-ui-compatibility, .mindar-ui-scanning');
    existingElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });

    isGlobalSetup = false;
    productsSetup = false;
    globalCleanupInProgress = false;
  };


  const cancelPendingAnimations = () => {
    pendingAnimationsRef.current.forEach(timeoutId => {
      clearTimeout(timeoutId);
    });
    pendingAnimationsRef.current = [];

    satelliteGroupsRef.current.forEach(satelliteGroup => {
      satelliteGroup.children.forEach(satelliteScene => {
        if (satelliteScene.userData && satelliteScene.userData.type === 'satelliteGroup') {
          satelliteScene.visible = false;

          satelliteScene.traverse((child) => {
            if (child.userData && child.userData.animationId) {
              cancelAnimationFrame(child.userData.animationId);
              child.userData.animationId = null;
            }

            if (child.isMesh) {
              child.visible = false;
              if (child.material) {
                child.material.opacity = 0;
              }
            }
          });
        }
      });
    });

    setSatellitesVisible(false);
  };


  const addPendingTimeout = (timeoutId) => {
    pendingAnimationsRef.current.push(timeoutId);
  };


  const removePendingTimeout = (timeoutId) => {
    const index = pendingAnimationsRef.current.indexOf(timeoutId);
    if (index > -1) {
      pendingAnimationsRef.current.splice(index, 1);
    }
  };


  const hideSatellites = (skipUIReset = false) => {
    satelliteGroupsRef.current.forEach(satelliteGroup => {
      satelliteGroup.children.forEach(satelliteScene => {
        if (satelliteScene.userData && satelliteScene.userData.type === 'satelliteGroup') {
          satelliteScene.children.forEach(child => {
            if (child.isMesh) {
              const fadeOut = () => {
                child.material.opacity -= 0.1;
                if (child.material.opacity <= 0) {
                  child.visible = false;
                  child.material.opacity = 0;
                } else {
                  requestAnimationFrame(fadeOut);
                }
              };
              fadeOut();
            }
          });
        }
      });
    });

    setSatellitesVisible(false);

    if (!skipUIReset) {
      selectedProductRef.current = null;
    }
  };


  const toggleSatellites = () => {
    const newVisibility = !satellitesVisible;
    setSatellitesVisible(newVisibility);

    satelliteGroupsRef.current.forEach(satelliteGroup => {
      satelliteGroup.children.forEach(satelliteScene => {
        if (satelliteScene.userData && satelliteScene.userData.type === 'satelliteGroup') {
          satelliteScene.visible = newVisibility;
        }
      });
    });

  };


  const setupAR = async () => {
    const registry = window.MINDAR_GLOBAL_REGISTRY;


    registry.cleanupInProgress = true;
    registry.isSetup = true;
    registry.containerId = containerRef.current?.id || 'mindar-container';

    isSetupRef.current = true;
    isGlobalSetup = true;

    try {
      cleanupExistingMindAR();

      const { MindARThree } = await import('mind-ar/dist/mindar-image-three.prod.js');

      // Hide the container initially to prevent camera flash
      if (containerRef.current) {
        containerRef.current.style.visibility = 'hidden';
      }

      const mindarThree = new MindARThree({
        container: containerRef.current,
        imageTargetSrc: './targets.mind',
        filterMinCF: 0.0001,
        filterBeta: 0.01,
        uiLoading: false,
        uiScanning: false,
        uiError: false
      });

      mindArRef.current = mindarThree;
      globalARInstance = mindarThree;

      registry.instance = mindarThree;
      registry.cleanupInProgress = false;

      const { renderer, scene, camera } = mindarThree;

      camera.fov = 60;
      camera.updateProjectionMatrix();

      const raycaster = new THREE.Raycaster();

      const anchors = [];
      const numberOfTargets = 7;

      for (let i = 0; i < numberOfTargets; i++) {
        const anchor = mindarThree.addAnchor(i);
        anchors.push(anchor);
      }


      const checkGlobalTracking = () => {
        let anyAnchorVisible = false;
        anchors.forEach(anchor => {
          if (anchor.visible) {
            anyAnchorVisible = true;
          }
        });

        if (anyAnchorVisible !== isTrackingRef.current) {
          isTrackingRef.current = anyAnchorVisible;
          setIsTracking(anyAnchorVisible);
        }

        requestAnimationFrame(checkGlobalTracking);
      };

      checkGlobalTracking();


      const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 2.0);
      directionalLight.position.set(0, 2, 5);
      directionalLight.castShadow = false;
      scene.add(directionalLight);

      const pointLight1 = new THREE.PointLight(0xffffff, 1.2, 8);
      pointLight1.position.set(-1, 2, 3);
      pointLight1.castShadow = false;
      scene.add(pointLight1);

      const pointLight2 = new THREE.PointLight(0xffffff, 1.2, 8);
      pointLight2.position.set(1, 2, 3);
      pointLight2.castShadow = false;
      scene.add(pointLight2);

      const spotLight = new THREE.SpotLight(0xffffff, 1.5, 10, Math.PI / 4, 0.3, 1);
      spotLight.position.set(0, 3, 4);
      spotLight.target.position.set(0, 0, 0);
      spotLight.castShadow = false;
      scene.add(spotLight);
      scene.add(spotLight.target);

      const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x888888, 0.6);
      scene.add(hemisphereLight);


      await setupMultiTargetProducts(anchors, renderer, scene, camera, raycaster);

      hideSatellitesRef.current = hideSatellites;


      setupEventListeners(raycaster, camera, scene, anchors);

      await mindarThree.start();

      renderer.setAnimationLoop(() => {
        renderer.render(scene, camera);
      });

      // Show the container after everything is ready
      if (containerRef.current) {
        containerRef.current.style.visibility = 'visible';
      }

      setIsARReady(true);

    } catch (error) {
      console.error('Failed to start MindAR:', error);
      isSetupRef.current = false;
      isGlobalSetup = false;
      globalARInstance = null;
    }
  };


  const cleanup = () => {
    const registry = window.MINDAR_GLOBAL_REGISTRY;

    registry.cleanupInProgress = true;
    globalCleanupInProgress = true;

    if (pendingAnimationsRef.current.length > 0) {
      pendingAnimationsRef.current.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      pendingAnimationsRef.current = [];
    }

    if (satelliteGroupsRef.current.length > 0) {
      satelliteGroupsRef.current.forEach(satelliteGroup => {
        if (satelliteGroup && satelliteGroup.children) {
          satelliteGroup.children.forEach(child => {
            if (child && child.traverse) {
              child.traverse((obj) => {
                if (obj.isMesh) {
                  if (obj.geometry) obj.geometry.dispose();
                  if (obj.material) {
                    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
                    materials.forEach((material) => {
                      if (material.map) material.map.dispose();
                      if (material.normalMap) material.normalMap.dispose();
                      if (material.roughnessMap) material.roughnessMap.dispose();
                      if (material.metalnessMap) material.metalnessMap.dispose();
                      if (material.emissiveMap) material.emissiveMap.dispose();
                      material.dispose();
                    });
                  }
                }
              });
            }
          });
        }
      });
      satelliteGroupsRef.current = [];
    }

    selectedProductRef.current = null;
    hideSatellitesRef.current = null;

    removeEventListeners();

    if (mindArRef.current) {
      const mindarThree = mindArRef.current;

      try {
        const scene = mindarThree.scene;
        if (scene) {
          scene.traverse((object) => {
            if (object.userData && object.userData.cleanup) {
              object.userData.cleanup();
            }
          });
        }

        if (mindarThree.renderer) {
          mindarThree.renderer.setAnimationLoop(null);
        }

        mindarThree.stop();

        const mindArElements = document.querySelectorAll('.mindar-ui-overlay, .mindar-ui-loading, .mindar-ui-compatibility, .mindar-ui-scanning');
        mindArElements.forEach(element => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });

      } catch (error) {
        console.error('Error during MindAR cleanup:', error);
      }

      mindArRef.current = null;
    }

    isSetupRef.current = false;
    isGlobalSetup = false;
    globalARInstance = null;
    productsSetup = false;
    globalCleanupInProgress = false;

    registry.instance = null;
    registry.isSetup = false;
    registry.containerId = null;
    registry.cleanupInProgress = false;

    setIsARReady(false);
  };


  useEffect(() => {
    const registry = window.MINDAR_GLOBAL_REGISTRY;
    const containerId = containerRef.current?.id || 'mindar-container';


    if (registry.cleanupInProgress || globalCleanupInProgress) {
      const waitForCleanup = () => {
        if (!registry.cleanupInProgress && !globalCleanupInProgress) {
          setTimeout(() => {
            if (!registry.isSetup && !registry.instance && !isSetupRef.current) {
              setupAR();
            }
          }, 100);
        } else {
          setTimeout(waitForCleanup, 50);
        }
      };
      waitForCleanup();
      return;
    }

    if (registry.instance || registry.isSetup) {

      if (registry.instance) {
        try {
          registry.instance.stop();
        } catch (error) {
          console.error('Error stopping existing registry instance:', error);
        }
      }

      const mindArElements = document.querySelectorAll('.mindar-ui-overlay, .mindar-ui-loading, .mindar-ui-compatibility, .mindar-ui-scanning');
      mindArElements.forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });

      registry.instance = null;
      registry.isSetup = false;
      registry.containerId = null;
    }

    if (isSetupRef.current) {
      return;
    }

    if (!containerRef.current) {
      return;
    }

    setupAR();

    return cleanup;
  }, []);

  const triggerSatelliteAnimations = (satelliteGroup, centeredProduct = null, anchorIndex = null, anchors) => {
    if (anchorIndex !== null && !anchors[anchorIndex].visible) {
      return;
    }
    setSatellitesVisible(true);

    if (anchorIndex !== null) {
      const productGroup = anchors[anchorIndex].group.children.find(child =>
        child.userData?.isProductGroup && child.userData.anchorIndex === anchorIndex
      );

      if (productGroup) {
        const products = productGroup.userData.products;
        const originalProduct = products.find(p => p.userData.productData.id === 'original');

        if (originalProduct && originalProduct.visible) {
          animateOriginalProductUp(originalProduct);
        }
      }
    }

    if (satelliteGroup) {
      satelliteGroup.children.forEach((satelliteScene) => {
        if (satelliteScene.userData && satelliteScene.userData.type === 'satelliteGroup') {
          if (centeredProduct) {
            satelliteScene.position.copy(centeredProduct.position);
            satelliteScene.position.y -= .5;
            satelliteScene.position.z += .2;
          }

          satelliteScene.visible = true;

          satelliteScene.traverse((child) => {
            if (child.isMesh && child.material) {
              child.material.transparent = true;
              child.material.opacity = 0;
              child.visible = false;

              if (child.userData && child.userData.originalPosition) {
                child.position.copy(child.userData.originalPosition);
              }
              if (child.userData && child.userData.originalScale) {
                child.scale.copy(child.userData.originalScale);
              }
              if (child.userData && child.userData.originalRotation) {
                child.rotation.copy(child.userData.originalRotation);
              }
            }
          });

          const timeoutId = setTimeout(() => {
            removePendingTimeout(timeoutId);

            if (anchorIndex !== null && !anchors[anchorIndex].visible) {
              return;
            }

            satelliteScene.traverse((child) => {
              if (child.isMesh && child.name && SATELLITE_ANIMATIONS[child.name]) {
                const animationConfig = SATELLITE_ANIMATIONS[child.name];
                animateSatelliteMesh(child, animationConfig, anchorIndex, addPendingTimeout, removePendingTimeout, anchors);
              }
            });

          }, 10);
          addPendingTimeout(timeoutId);
        }
      });
    }
  };


  const setupMultiTargetProducts = async (anchors, renderer, scene, camera, raycaster) => {
    if (productsSetup) {
      return;
    }

    productsSetup = true;
    const loader = new GLTFLoader();

    anchors.forEach((anchor, anchorIndex) => {
      const existingProducts = anchor.group.children.filter(child => child.userData?.isProductGroup);
      if (existingProducts.length > 0) {
        existingProducts.forEach(group => anchor.group.remove(group));
      }

      const productGroup = new THREE.Group();
      productGroup.userData.isProductGroup = true;
      productGroup.userData.anchorIndex = anchorIndex;
      const productModels = [];

      const positions = [
        { x: -0.3, y: 0.1, z: 0 },
        { x: 0.3, y: 0.1, z: 0 }
      ];

      for (let i = 0; i < 2; i++) {
        loader.load(`/${PRODUCTS[i].model}`, (gltf) => {
          const product = gltf.scene.clone();

          product.traverse((child) => {
            if (child.isMesh && child.name === 'para_tus_axilas') {
              child.visible = false;
              child.userData.isParaTusAxilas = true;
            }

            if (child.isMesh && child.name === 'for_all_the_body') {
              child.visible = false;
              child.userData.isForAllTheBody = true;
            }

            if (child.isMesh && child.material) {
              const materials = Array.isArray(child.material) ? child.material : [child.material];

              materials.forEach((material) => {
                if (PRODUCTS[i].model === 'dove_blue.glb' && material.name === 'tapa.001') {
                  material.transparent = true;
                  material.opacity = 0.7;
                  material.alphaTest = 0.1;
                }

                if (material.isMeshStandardMaterial || material.isMeshLambertMaterial || material.isMeshPhongMaterial) {
                  const originalColor = material.color.clone();

                  material.emissive = new THREE.Color(0x111111);
                  material.emissiveIntensity = 0.3;

                  material.color = originalColor.clone();
                  material.color.multiplyScalar(1.2);

                  if (material.map) {
                    material.map.generateMipmaps = false;
                    material.map.minFilter = THREE.NearestFilter;
                    material.map.magFilter = THREE.NearestFilter;
                    material.map.wrapS = THREE.ClampToEdgeWrapping;
                    material.map.wrapT = THREE.ClampToEdgeWrapping;
                    material.map.flipY = false;
                    material.map.needsUpdate = true;
                  }

                  if (material.isMeshStandardMaterial) {
                    material.metalness = 0.1;
                    material.roughness = 0.4;
                    material.envMapIntensity = 0.5;
                  }

                  if (material.normalMap) {
                    material.normalScale.setScalar(1.0);
                  }

                  if (material.transparent) {
                    material.alphaTest = 0.1;
                  }

                  material.needsUpdate = true;
                }
              });
            }
          });

          product.scale.setScalar(0.06);
          product.position.set(0, 0.1, 0);
          product.rotation.x = Math.PI / 2;
          product.visible = false;

          product.userData = {
            id: `product_${PRODUCTS[i].id}_anchor_${anchorIndex}`,
            name: PRODUCTS[i].name,
            productData: PRODUCTS[i],
            anchorIndex: anchorIndex,
            productIndex: i,
            finalPosition: positions[i],
            hasAnimated: false
          };

          productModels.push(product);
          productGroup.add(product);
        });
      }

      productGroup.userData.products = productModels;
      anchor.group.add(productGroup);

      let lastVisibilityState = false;
      let stabilityTimer = null;

      const checkTarget = () => {
        const currentVisible = anchor.visible;

        if (currentVisible !== lastVisibilityState) {
          lastVisibilityState = currentVisible;

          if (stabilityTimer) {
            clearTimeout(stabilityTimer);
            stabilityTimer = null;
          }

          if (currentVisible) {
            stabilityTimer = setTimeout(() => {
              if (anchor.visible) {
                showProducts(productGroup, anchors);
              }
              stabilityTimer = null;
            }, 100);
          } else {
            cancelPendingAnimations();
            hideSatellites(false);
            setSatellitesVisible(false);
            hideProducts(productGroup);
          }
        }

        requestAnimationFrame(checkTarget);
      };
      checkTarget();
    });
  };

  const showProducts = (productGroup, anchors) => {
    const products = productGroup.userData.products;
    if (!products) return;

    const anchorIndex = productGroup.userData.anchorIndex;

    products.forEach((product) => {
      if (!product.visible) {
        product.visible = true;
        product.position.set(0, 0.1, 0);
      }
    });

    setTimeout(() => {

      products.forEach((product) => {
        const isBodyProductSelected = product.userData.productData.id === 'body' &&
                                     selectedProductRef.current &&
                                     selectedProductRef.current.id === 'body';

        if (isBodyProductSelected) {
          product.userData.hasAnimated = true;
          return;
        }

        if (!product.userData.hasAnimated) {
          product.userData.hasAnimated = true;
          animateProductToPosition(product, product.userData.finalPosition);
        }

        // Start spinning animations for specific submeshes
        if (product.userData.productData.id === 'body') {
          startSubmeshSpin(product, ['model_0003', 'model_0003_1'], 0.02);
        } else if (product.userData.productData.id === 'original') {
          startSubmeshSpin(product, ['model_0005', 'model_0005_1'], 0.02);
        }
      });

      // Auto-trigger satellites for body product after 1 second
      const bodyProduct = products.find(p => p.userData.productData.id === 'body');
      const originalProduct = products.find(p => p.userData.productData.id === 'original');
      
      if (bodyProduct && originalProduct && anchors) {
        const timeoutId = setTimeout(() => {
          removePendingTimeout(timeoutId);
          
          // Set the selected product
          selectedProductRef.current = bodyProduct.userData.productData;
          
          // Animate products: body to center, original to back
          let animationsCompleted = 0;
          const totalAnimations = 2;

          const onAnimationComplete = () => {
            animationsCompleted++;
            if (animationsCompleted === totalAnimations) {
              const satelliteTimeoutId = setTimeout(() => {
                removePendingTimeout(satelliteTimeoutId);
                
                // Auto-trigger satellite animations after product animations complete
                const anchorIndex = productGroup.userData.anchorIndex;
                let satelliteGroup = anchors[anchorIndex].group.children.find(child =>
                  child.userData?.isSatelliteGroup && child.userData.anchorIndex === anchorIndex
                );

                if (satelliteGroup && satelliteGroup.children.length > 0 && satelliteGroup.children[0].userData?.isLoaded) {
                  triggerSatelliteAnimations(satelliteGroup, bodyProduct, anchorIndex, anchors);
                } else {
                  if (!satelliteGroup) {
                    satelliteGroup = createSatelliteObjects(bodyProduct, anchorIndex, (group) => {
                      triggerSatelliteAnimations(group, bodyProduct, anchorIndex, anchors);
                    }, true, anchors);
                  }
                }
              }, 100);
              addPendingTimeout(satelliteTimeoutId);
            }
          };

          animateProductToCenter(bodyProduct, onAnimationComplete);
          animateOriginalProductOut(originalProduct, onAnimationComplete);
        }, 1000);
        addPendingTimeout(timeoutId);
      }
    }, 300);
  };

  const hideProducts = (productGroup) => {
    const products = productGroup.userData.products;
    if (!products) return;


    const anchorIndex = productGroup.userData.anchorIndex;
    if (anchorIndex !== undefined) {
      cancelPendingAnimations();
      hideSatellites(false);
    }

    products.forEach(product => {
      product.visible = false;
      product.rotation.x = Math.PI / 2;
      product.rotation.y = 0;
      product.position.set(0, 0.1, 0);

      const isBodyProductSelected = product.userData.productData.id === 'body' &&
                                   selectedProductRef.current &&
                                   selectedProductRef.current.id === 'body';

      if (!isBodyProductSelected) {
        product.userData.hasAnimated = false;
      } else {
      }

      // Stop spinning animations for specific submeshes
      if (product.userData.productData.id === 'body') {
        stopSubmeshSpin(product, ['model_0003', 'model_0003_1']);
      } else if (product.userData.productData.id === 'original') {
        stopSubmeshSpin(product, ['model_0005', 'model_0005_1']);
      }

      product.traverse((child) => {
        if (child.isMesh && child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((material) => {
            material.opacity = 1;

            if (product.userData.productData.id === 'original' && material.name !== 'tapa.001') {
              material.transparent = false;
            }
            material.needsUpdate = true;
          });
        }

        if (child.isMesh && child.name === 'para_tus_axilas') {
          child.visible = false;
        }

        if (child.isMesh && child.name === 'for_all_the_body') {
          child.visible = false;
        }
      });
    });
  };

  const handleSelection = (x, y, raycaster, camera, scene, anchors) => {
    raycaster.setFromCamera({ x, y }, camera);
    const intersects = raycaster.intersectObjects(scene.children, true);

    let anySatellitesVisible = false;
    satelliteGroupsRef.current.forEach(satelliteGroup => {
      satelliteGroup.children.forEach(satelliteScene => {
        if (satelliteScene.userData && satelliteScene.userData.type === 'satelliteGroup' && satelliteScene.visible) {
          anySatellitesVisible = true;
        }
      });
    });

    if (intersects.length > 0) {
      for (let i = 0; i < intersects.length; i++) {
        let object = intersects[i].object;
        while (object && !object.userData?.id?.startsWith('product_')) {
          object = object.parent;
        }

        if (object && object.userData?.id?.startsWith('product_')) {
          if (object.userData.productData.id === 'original') {
            return;
          }
          // Only trigger product info modal, no satellite handling
          if (!disableProductInfoClick) {
            const currentSelectedProduct = selectedProductRef.current;
            if (currentSelectedProduct) {
              onProductSelect(currentSelectedProduct);
            }
          }
          return;
        }
      }
    }

    if (anySatellitesVisible && !disableProductInfoClick) {
      const currentSelectedProduct = selectedProductRef.current;

      // Only trigger product info when satellites are visible and clicked
      setTimeout(() => {
        if (currentSelectedProduct && selectedProductRef.current) {
          onProductSelect(currentSelectedProduct);
        }
      }, 100);
      return;
    }
  };


  const createSatelliteObjects = (product, anchorIndex, onLoadedCallback, useCurrentPosition = false, anchors) => {
    const productId = product.userData.productData.id;
    const satelliteConfig = SATELITE_OBJECTS.find(config => config.id === productId);

    if (!satelliteConfig) return;

    const loader = new GLTFLoader();
    const satelliteGroup = new THREE.Group();
    satelliteGroup.userData.isSatelliteGroup = true;
    satelliteGroup.userData.anchorIndex = anchorIndex;

    loader.load(`/${satelliteConfig.model}`, (gltf) => {

      const meshMap = new Map();
      gltf.scene.traverse((child) => {
        if (child.isMesh && child.name) {
          meshMap.set(child.name, child);
        }
      });

      const satelliteScene = gltf.scene.clone();

      if (useCurrentPosition) {
        satelliteScene.position.copy(product.position);
      } else {
        satelliteScene.position.copy(product.position);
      }

      satelliteScene.position.y -= .5;
      satelliteScene.position.z += .2;
      satelliteScene.rotation.x = Math.PI / 2;
      satelliteScene.scale.set(0.1, 0.1, 0.1);
      satelliteScene.visible = false;

      satelliteScene.traverse((child) => {
        if (child.isMesh && child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];

          materials.forEach((material) => {
            if (material.isMeshStandardMaterial || material.isMeshLambertMaterial || material.isMeshPhongMaterial) {
              const originalColor = material.color.clone();

              material.emissive = new THREE.Color(0x050505);
              material.emissiveIntensity = 0.1;

              material.color = originalColor.clone();
              material.color.multiplyScalar(1.5);
              material.color.setHSL(
                material.color.getHSL({}).h,
                Math.min(1.0, material.color.getHSL({}).s * 1.3),
                Math.min(1.0, material.color.getHSL({}).l * 1.1)
              );

              if (material.map) {
                material.map.generateMipmaps = false;
                material.map.minFilter = THREE.LinearFilter;
                material.map.magFilter = THREE.LinearFilter;
                material.map.wrapS = THREE.ClampToEdgeWrapping;
                material.map.wrapT = THREE.ClampToEdgeWrapping;
                material.map.flipY = false;
                material.map.needsUpdate = true;
              }

              if (material.isMeshStandardMaterial) {
                material.metalness = 0.0;
                material.roughness = 0.9;
                material.envMapIntensity = 0.2;
              }

              material.needsUpdate = true;

              if (material.normalMap) {
                material.normalScale.setScalar(1.0);
              }

              if (material.transparent) {
                material.alphaTest = 0.1;
              }

              material.needsUpdate = true;
            }
          });

          child.material.transparent = true;
          child.material.opacity = 0;
          child.visible = false;

          child.userData = child.userData || {};
          child.userData.originalPosition = child.position.clone();
          child.userData.originalScale = child.scale.clone();
          child.userData.originalRotation = child.rotation.clone();
        }
      });

      satelliteScene.userData = {
        type: 'satelliteGroup',
        productId: productId,
        anchorIndex: anchorIndex,
        isSatelliteGroup: true,
        isLoaded: true
      };

      satelliteGroup.add(satelliteScene);


      if (onLoadedCallback) {
        onLoadedCallback(satelliteGroup);
      }

    }, undefined, (error) => {
      console.error(`Failed to load ${satelliteConfig.model}:`, error);
    });

    anchors[anchorIndex].group.add(satelliteGroup);
    satelliteGroupsRef.current.push(satelliteGroup);

    return satelliteGroup;
  };

  return {
    isARReady,
    satellitesVisible,
    isTracking,
    toggleSatellites,
    cleanup
  };
};

